import { useCallback, useState } from "react";

type AutoFocusHandler = (event: Event) => void;

/**
 * FP-112 (BEN-141): closing an overlay dropped keyboard focus to `<body>`.
 *
 * Radix's modal Dialog cancels FocusScope's own restore on close and focuses
 * `context.triggerRef` instead. That ref is set only by `<DialogTrigger>`. Almost
 * every dialog, sheet and side panel here is opened from a plain button with a
 * controlled `open`, or mounts already open (the terms dialog after a 409), so the
 * ref is empty and focus fell to the page. A keyboard or screen-reader user then
 * starts again from the top. Measured on the terms dialog, Forecast settings, the
 * welcome guide, the feedback widget, all 6 FA modals, transaction detail, the
 * categorise wizard, Recurring's Add dialog and the rule explainer.
 *
 * This records what had focus when the overlay opened (FocusScope fires the open
 * event before it moves focus in) and gives focus back to it on close, if it is
 * still in the page and can take it. Otherwise Radix's default runs unchanged. A
 * caller's own handler runs first and can `preventDefault()` to opt out.
 */
export function useReturnFocus(
  onOpenAutoFocus?: AutoFocusHandler,
  onCloseAutoFocus?: AutoFocusHandler,
): { onOpenAutoFocus: AutoFocusHandler; onCloseAutoFocus: AutoFocusHandler } {
  // One core per mounted overlay, so the opener survives re-renders.
  const [rf] = useState(() => {
    trackRecentFocus();
    return createReturnFocus(() => document);
  });
  const handleOpen = useCallback(
    (event: Event) => rf.open(event, onOpenAutoFocus),
    [rf, onOpenAutoFocus],
  );
  const handleClose = useCallback(
    (event: Event) => rf.close(event, onCloseAutoFocus),
    [rf, onCloseAutoFocus],
  );
  return { onOpenAutoFocus: handleOpen, onCloseAutoFocus: handleClose };
}

type FocusTarget = Pick<HTMLElement, "focus" | "isConnected" | "closest"> & { disabled?: boolean };
type FocusDoc = { activeElement: unknown; body: unknown };
/** `target` is the overlay's content node: Radix's FocusScope dispatches the event on it. */
type AutoFocusEvent = Pick<Event, "defaultPrevented" | "preventDefault"> & { target?: unknown };
type Container = { contains: (other: never) => boolean };

/**
 * FP-166 (mobile's trace, 2026-10-07): a field with `autoFocus` inside the overlay takes
 * focus while React mounts it, BEFORE Radix fires `onOpenAutoFocus`. The opener read at
 * that moment was the field itself, inside the closing panel, so nothing was restored and
 * focus fell to <body> (/rules "New Rule", Forecast "Add bucket", "Log a contribution").
 * So the page's recent focus is kept, newest last, and when focus is already inside the
 * overlay the opener is the newest element outside it.
 */
const RECENT_FOCUS_LIMIT = 8;
const recentFocus: FocusTarget[] = [];
let tracking = false;

function trackRecentFocus(): void {
  if (tracking || typeof document === "undefined") return;
  tracking = true;
  document.addEventListener(
    "focusin",
    (event) => {
      const el = event.target as FocusTarget | null;
      if (!el || typeof el.focus !== "function") return;
      const at = recentFocus.indexOf(el);
      if (at !== -1) recentFocus.splice(at, 1);
      recentFocus.push(el);
      if (recentFocus.length > RECENT_FOCUS_LIMIT) recentFocus.shift();
    },
    true,
  );
}

function isContainer(node: unknown): node is Container {
  return typeof (node as Container | null)?.contains === "function";
}

/** The opener-recording core, free of React and of a real DOM so it can be tested. */
export function createReturnFocus(
  getDoc: () => FocusDoc,
  getRecent: () => readonly FocusTarget[] = () => recentFocus,
) {
  let opener: FocusTarget | null = null;
  // Whether Radix fired the open event. It does NOT when focus is already inside the
  // container at mount (FocusScope's `hasFocusedCandidate`), which is exactly the
  // autoFocus case (FP-166), so the opener is then found at close instead.
  let opened = false;

  /** The newest recently focused element that is in the page and outside `container`. */
  function newestOutside(container: unknown, body: unknown): FocusTarget | null {
    if (!isContainer(container)) return null;
    const recent = getRecent();
    for (let i = recent.length - 1; i >= 0; i -= 1) {
      const el = recent[i];
      if (el !== body && el.isConnected && !container.contains(el as never)) return el;
    }
    return null;
  }

  return {
    open(event: AutoFocusEvent, caller?: (event: never) => void) {
      opened = true;
      const doc = getDoc();
      let active = doc.activeElement as FocusTarget | null;
      const container = event.target;
      if (active && isContainer(container) && container.contains(active as never)) {
        active = newestOutside(container, doc.body);
      }
      opener = active && active !== doc.body && typeof active.focus === "function" ? active : null;
      caller?.(event as never);
    },
    close(event: AutoFocusEvent, caller?: (event: never) => void) {
      caller?.(event as never);
      const target = opened ? opener : newestOutside(event.target, getDoc().body);
      opener = null;
      opened = false;
      if (event.defaultPrevented || !canTakeFocus(target)) return;
      event.preventDefault();
      target.focus();
    },
  };
}

/** In the page, enabled, and not inside an inert subtree. */
export function canTakeFocus(el: FocusTarget | null): el is FocusTarget {
  if (!el || !el.isConnected) return false;
  if (el.disabled) return false;
  return el.closest("[inert]") === null;
}
